/**
 * Rule: a SYSTEM step must have at least one action attached.
 *
 * A system step (a plain `bpmn:Task`) exists to run a server action — an empty
 * one does NOTHING and is an invalid pass-through node. This ships when a build
 * folds several operations into one action and leaves the sibling system steps
 * empty (KOA-17362: `ApproveAccountingReturn` did the order-status update + the
 * return-record insert, so the separate "Sipariş Durumunu İade Onaylandı Yap"
 * and "İade Kaydı Oluştur" steps were created but never given an action —
 * `actionCount: 0`, dead nodes on the canvas).
 *
 * Scope — SYSTEM STEPS ONLY. Kuika creates a system step as a plain `bpmn:Task`
 * (`workflowCreateSystemStepAction`) and a user task as `bpmn:UserTask`
 * (which, since UserTask extends Task, would match `is(node, "bpmn:Task")`).
 * So we gate on the EXACT `$type === "bpmn:Task"`: user tasks (approval/form
 * screens legitimately carry no server action — including a fresh workflow's
 * `<userTask>` "Sample Step" skeleton), gateways, events, service/script tasks,
 * sub-processes and call activities are all left untouched.
 *
 * The action list lives on the in-memory JSON step (`step.actions`), keyed by
 * the BPMN node id — resolved through the workflow provider exactly like
 * `screen-assigned`. CONSERVATIVE: we report only when the step is FOUND and its
 * action list is empty. When the workflow context is not ready or the JSON step
 * cannot be resolved (a transient load / XML<->JSON desync) we SKIP, so this
 * error-level rule never fires a false positive mid-load; the linter re-runs on
 * the next diagram event once the model settles.
 */
const { is } = require("bpmnlint-utils");

function resolveStep(ctx, nodeId) {
  const sd = ctx && ctx.providerInstance ? ctx.providerInstance : null;
  if (!sd) return { ready: false, step: null };

  const workflowProvider = sd.workflowProvider ? sd.workflowProvider : null;
  const ctxValues =
    typeof sd.getScreenDesignContextProviderValues === "function" ? sd.getScreenDesignContextProviderValues() : null;
  const selectedWf =
    (workflowProvider && typeof workflowProvider.getSelectedWf === "function" && workflowProvider.getSelectedWf()) ||
    (ctxValues && ctxValues.selectedWf) ||
    null;
  if (!selectedWf) return { ready: false, step: null };

  let step = null;
  if (workflowProvider && typeof workflowProvider.findStepRecursively === "function") {
    try {
      step = workflowProvider.findStepRecursively(selectedWf, (s) => s && s.id === nodeId);
    } catch {
      step = null;
    }
  }
  if (!step) {
    const steps = ctx && typeof ctx.getWfSteps === "function" ? ctx.getWfSteps() : undefined;
    if (Array.isArray(steps)) step = steps.find((s) => s && s.id === nodeId) || null;
  }
  return { ready: true, step };
}

module.exports = function systemStepHasAction({ ctx }) {
  return {
    check(node, reporter) {
      if (!node) return;
      const bo = node.businessObject || node;
      // System steps are plain bpmn:Task. Gate on the EXACT type so user tasks
      // (bpmn:UserTask extends bpmn:Task and would match is()) and every other
      // node kind are excluded.
      if (bo.$type !== "bpmn:Task") return;
      // Defensive: never touch a sub-process / call activity that happens to be
      // modelled as a Task subtype.
      if (is(node, "bpmn:SubProcess") || is(node, "bpmn:CallActivity")) return;

      const { ready, step } = resolveStep(ctx, node.id);
      // Not ready / step unresolved → skip (no error-level false positive).
      if (!ready || !step) return;

      const actions = Array.isArray(step.actions) ? step.actions : [];
      if (actions.length === 0) {
        reporter.report(
          node.id,
          "This system step has no action, so it does nothing when the process runs. " +
            "Attach the action it is meant to perform, or remove the step."
        );
      }
    }
  };
};
